import '../../styles/_survey.scss';

import React, { SyntheticEvent, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { SFSurveyQuestion } from '../../datasource/generated';
import { RadioInput } from '../RadioInput/RadioInput';
import { Rating } from '../Rating/Rating';
import TextArea from '../TextArea/TextArea';
import Toggle from '../Toggle/Toggle';
import { isQuestionRequired,surveyContext,SurveyQuestionType } from './utils';

type ISurveyQuestionProps = {
    question: SFSurveyQuestion,
}

export const SurveyQuestion = ({ question }:ISurveyQuestionProps) => {
    const inputName = `question.sk_${question.sk}`;

    const { formErrors } = useContext(surveyContext);

    const questionError = useMemo(() => {
        return formErrors.find(err => err.path.join('.') === inputName);
    },[formErrors, inputName]);

    const container = useRef<HTMLDivElement>(null);
    const [applicableState, setApplicableState] = useState(false);
    const applicableLabel = applicableState ? 'Not applicable' : 'Applicable';
    const [textAnswer, setTextAnswer] = useState('');
    const [ratingValue, setRatingValue] = useState<string | number | null>(null);

    useEffect(() => {
        if(!applicableState)
            return;
        container.current?.querySelectorAll<HTMLInputElement>('input[type=radio]').forEach((input) => {
            input.checked = false;
        });
    },[applicableState]);

    const customStyle = question.surveyQuestionTitle === 'Overall Comments' ? true : false;
    const ratingGroup = question.surveyQuestionType === SurveyQuestionType.RADIO_GROUP_RATING ? true : false;

    const handleToggleChange = (e: SyntheticEvent<HTMLInputElement>) => {
        const { checked, value } = e.currentTarget;
        setApplicableState(checked);
        setRatingValue(value);
    };

    const handleRadioInputChange = () => {
        setApplicableState(false);
    };
    
    const handleTextAreaChange = (value: string) => {        
        setTextAnswer(value);       
    };
    
    return (
        <div ref={container} className="survey-question">
            <p style={{ color:  ratingGroup || customStyle ? 'white': 'silver', 
                paddingTop: ratingGroup || customStyle ? '56px' : '',
                fontSize: customStyle ? '24px' : '', 
                fontWeight: customStyle ? '700' : '', 
                lineHeight: customStyle ? '32px' : '',
                letterSpacing: customStyle ? '-0.02em' : '',
                marginBottom: customStyle ? '36px' : ''

            }}>
                {question.surveyQuestionText}
                {isQuestionRequired(question) && '*'}
            </p>
            <div>
                {question.surveyQuestionType === SurveyQuestionType.RADIO_GROUP && (
                    <ul>
                        <div className='radio-group'>
                            {question.surveyQuestionChoices?.map((choice) => (
                                <div key={choice.sk}>
                                    <RadioInput                                                                                  
                                        name={inputName}
                                        value={choice.surveyQuestionChoiceValue || ''}
                                        onChange={handleRadioInputChange}
                                        required={isQuestionRequired(question)}
                                        variant={'default'}>
                                        {choice.surveyQuestionChoiceValue}
                                    </RadioInput>
                                </div>
                            ))}
                        </div>
                    </ul>
                )}
                {ratingGroup && (
                    <Rating
                        id={inputName}
                        name={inputName}
                        value={ratingValue}
                        onChange={(value) => setRatingValue(value)}
                        ratings={question.surveyQuestionChoices
                            ?.filter(c => c.surveyQuestionChoiceValue !== 'N/A')
                            .map((choice) => ({
                                value: choice.surveyQuestionChoiceValue ?? '',
                                label: choice.surveyQuestionChoiceTitle ?? ''
                            })) || []}
                        disabled={applicableState}
                    >
                        {question.surveyQuestionChoices?.filter(c => c.surveyQuestionChoiceValue === 'N/A').map((choice) => (
                            <div key={choice.sk} className="rating-extra">
                                <label 
                                    style={{ marginTop: '56px' }}
                                    htmlFor={inputName}>
                                            Not Applicable
                                </label>
                                <span style={{ marginLeft: 'auto', lineHeight: 'normal', marginTop: '56px' }}>
                                    <Toggle
                                        id={inputName}
                                        name={inputName}
                                        color='amethyst'
                                        ariaLabel={applicableLabel}
                                        checked={applicableState}
                                        onChange={(e) => handleToggleChange(e)}
                                        showCheckmark={false}
                                        size='large'
                                        required={isQuestionRequired(question)}
                                        value={choice.surveyQuestionChoiceValue ?? ''}
                                    />
                                </span>
                            </div>
                        ))}
                    </Rating>
                )}
                {question.surveyQuestionType === SurveyQuestionType.TEXT && (
                    <div className='survey__question-text'>

                        <TextArea
                            name={inputName}
                            updateValue={handleTextAreaChange}
                            currentValue={textAnswer}
                            maxLength={255}                            
                            showTextAreaError={false}                            
                            textAreaLabel='Add comment'
                            ariaLabel='255 characters maximum' 
                        />
                    
                    </div>
                )}
            </div>
            {
                questionError &&                       
                        <div className='survey__error'>
                            {questionError?.message}
                        </div>
            }
        </div>
    );
};